/**
 * PaperViewModal - Read-only view of a paper before highlighting
 *
 * Displays the paper content with metadata based on source type:
 * - TEXT: Teacher-pasted paper
 * - STUDENT_WRITING: Written in student portal
 * - EXTENSION: Imported from Google Docs (includes link to edit replay)
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Pill } from "@/components/Pill";
import { Spinner } from "@/components/Spinner";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";
import { useDocumentEditMetrics } from "@/api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import type { Document, DocumentSource } from "@markeroo/shared";
import { EditMetricsIndicator } from "./EditMetricsIndicator";
import {
  Editor,
  jsonContentToProseMirror,
  type JSONContent,
} from "@/features/editor/unified";
import PiPlayCircleStroke from "@markeroo/icons/src/stroke/media/PiPlayCircleStroke";
import PiFileTextStroke from "@markeroo/icons/src/stroke/files-&-folders/PiFileTextStroke";
import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import PiDrawPencilStroke from "@markeroo/icons/src/stroke/editing/PiDrawPencilStroke";

export interface PaperViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  studentName: string;
  /** Class ID for navigation to replay page */
  classId?: string;
  /** Assignment ID for navigation to replay page */
  assignmentId?: string;
  /** Callback to open the Writing Analysis modal */
  onOpenAnalysis?: () => void;
}

/**
 * Get a human-readable label for the document source.
 */
function getSourceLabel(source: DocumentSource): string {
  switch (source) {
    case "text":
      return ui.labels.sourceText;
    case "student_writing":
      return ui.labels.sourceStudentWriting;
    case "extension":
      return ui.labels.sourceExtension;
    default:
      return source;
  }
}

/**
 * Get the appropriate icon for the document source.
 */
function getSourceIcon(source: DocumentSource) {
  switch (source) {
    case "text":
      return <PiClipboardCheckStroke size={14} aria-hidden="true" />;
    case "student_writing":
      return <PiDrawPencilStroke size={14} aria-hidden="true" />;
    case "extension":
      return <PiFileTextStroke size={14} aria-hidden="true" />;
    default:
      return null;
  }
}

/**
 * Calculate word count from plain text.
 */
function getWordCount(text: string | null | undefined): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Format date for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PaperViewModal({
  isOpen,
  onClose,
  document,
  studentName,
  classId,
  assignmentId,
  onOpenAnalysis,
}: PaperViewModalProps) {
  const navigate = useNavigate();
  const aiDetectorEnabled = useFeatureFlag("ai_detector_extension");

  // Documents with edit history (extension and student_writing, but not text/pasted)
  const hasEditHistory = document?.source === "extension" || document?.source === "student_writing";

  // For documents with edit history, fetch edit metrics to show event count
  const { data: editMetrics, isLoading: isLoadingMetrics } =
    useDocumentEditMetrics(
      hasEditHistory ? assignmentId : undefined,
      hasEditHistory ? document?.id : undefined
    );

  // Convert bodyJson to ProseMirror node for the Editor
  // Type assertion via unknown: API returns Record<string, unknown> from Zod validation,
  // but we know it's JSONContent at runtime. TypeScript requires the double assertion.
  const proseMirrorDoc = useMemo(() => {
    if (!document?.bodyJson) return null;
    return jsonContentToProseMirror(document.bodyJson as unknown as JSONContent);
  }, [document?.bodyJson]);

  const wordCount = document ? getWordCount(document.bodyText) : 0;

  const handleViewReplay = () => {
    if (classId && assignmentId && document) {
      onClose();
      void navigate(
        `/classes/${classId}/assignments/${assignmentId}/papers/${document.id}/replay`
      );
    }
  };

  const canViewReplay =
    hasEditHistory &&
    classId &&
    assignmentId &&
    editMetrics &&
    editMetrics.totalEvents > 0;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" aria-label={ui.headers.paperView}>
      <ModalHeader
        title={
          <>
            {ui.headers.paperView}
            {studentName && (
              <>
                {" "}
                {ui.labels.for} {studentName}
              </>
            )}
          </>
        }
        onClose={onClose}
      />
      <ModalBody>
        {!document ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
            <span className="ml-3 text-sm text-theme-muted">
              {ui.loading.loadingPaper}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Metadata section */}
            <div className="flex flex-col gap-2">
              {/* Pills row */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Source badge */}
                <Pill variant="info" icon={getSourceIcon(document.source)}>
                  {getSourceLabel(document.source)}
                </Pill>

                {/* Status badge */}
                <Pill
                  variant={document.status === "final" ? "success" : "info"}
                  icon={document.status === "final" ? "✓" : "✎"}
                >
                  {document.status === "draft" ? ui.labels.draft : ui.labels.final}
                </Pill>

                {/* AI Risk indicator for documents with edit history */}
                {aiDetectorEnabled && hasEditHistory && assignmentId && (
                  <EditMetricsIndicator
                    assignmentId={assignmentId}
                    documentId={document.id}
                    onClick={onOpenAnalysis}
                  />
                )}
              </div>

              {/* Text metadata row */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-theme-muted">
                {/* Word count */}
                <span>
                  {wordCount.toLocaleString()} {ui.labels.words}
                </span>

                {/* Created date */}
                <span>
                  {formatDate(document.createdAt)}
                </span>

                {/* Edit events count for documents with edit history */}
                {hasEditHistory && (
                  <>
                    {isLoadingMetrics ? (
                      <Spinner size="sm" />
                    ) : editMetrics && editMetrics.totalEvents > 0 ? (
                      <span>
                        {editMetrics.totalEvents.toLocaleString()}{" "}
                        {ui.editMetrics.editEvents}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Paper content */}
            <div className="rounded-lg border border-border bg-white p-4 max-h-[50vh] overflow-y-auto">
              {proseMirrorDoc ? (
                <Editor
                  initialContent={proseMirrorDoc}
                  editable={false}
                  className="prose-sm"
                  ariaLabel="Paper content"
                />
              ) : document.bodyText ? (
                <div className="whitespace-pre-wrap text-sm text-theme-primary">
                  {document.bodyText}
                </div>
              ) : (
                <p className="text-sm text-theme-muted italic">
                  {ui.empty.noPapers}
                </p>
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={onClose}
        >
          {ui.actions.close}
        </button>
        {canViewReplay && (
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={handleViewReplay}
          >
            <PiPlayCircleStroke size={16} aria-hidden="true" />
            {ui.editMetrics.viewEditReplay}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
